import { type AuthToken } from '@reliance/types/models/Auth';
import axios, { type AxiosError } from 'axios';
import dayjs from 'dayjs';
import config from '../services/config';
import NotificationService, {
  NotificationSeverity,
} from '../services/NotificationService';
import { logout, refreshToken } from '../store/thunks/auth';
import { store } from '../store/store';
import ClientOAuth2 from 'client-oauth2';
import {
  type ApiService as ApiServiceType,
  type AccessToken,
  type QueryParams,
  type RequestOptions,
} from '../types/ApiService';

const oauthClient = new ClientOAuth2(config.auth);

class ApiService implements ApiServiceType {
  private url: string;
  private headers: Record<string, string>;
  private token: AccessToken | null;
  private refreshing: boolean = false;
  private type: 'site' | 'central' | null;

  constructor(url: string, type: 'site' | 'central' | null) {
    this.get = this.get.bind(this);
    this.post = this.post.bind(this);
    this.put = this.put.bind(this);
    this.delete = this.delete.bind(this);
    this.buildQuery = this.buildQuery.bind(this);
    this.buildUrl = this.buildUrl.bind(this);
    this.request = this.request.bind(this);
    this.url = url;
    this.headers = {};
    this.token = null;
    this.type = type;
  }

  setToken(token: AccessToken | null) {
    this.token = token;
  }

  get<R = unknown>(url: string, opts?: RequestOptions): Promise<R> {
    return this.request<R>('GET', url, opts || {});
  }

  post<Body = unknown, R = unknown>(
    url: string,
    data?: Body,
    opts?: RequestOptions,
  ): Promise<R> {
    return this.request<R>('POST', url, { ...opts, data });
  }

  put<Body = unknown, R = unknown>(
    url: string,
    data?: Body,
    opts?: RequestOptions,
  ): Promise<R> {
    return this.request<R>('PUT', url, { ...opts, data });
  }

  delete<R = unknown>(url: string, opts?: RequestOptions): Promise<R> {
    return this.request<R>('DELETE', url, opts || {});
  }

  private addParam(
    key: string,
    value: string | number | boolean,
    url: string,
    query: string,
  ) {
    if (query == null) {
      query = '';
    }
    if (query.length > 0 || url.indexOf('?') > -1) {
      query += '&';
    }
    if (query.length === 0 && url.indexOf('?') === -1) {
      query += '?';
    }
    query += key + '=' + encodeURIComponent(value);
    return query;
  }

  private buildQuery(url: string, queryParams: QueryParams | null | undefined) {
    if (queryParams == null) {
      queryParams = {};
    }
    let query = '';
    for (const key in queryParams) {
      const value = queryParams[key];
      if (value == null || value === '') {
        continue;
      }

      // Convert dayjs dates into the format PHP expects
      if (dayjs.isDayjs(value)) {
        query = this.addParam(
          key,
          value.format(config.phpDateFormat),
          url,
          query,
        );
      } else if (
        typeof value === 'object' &&
        'startDate' in value &&
        value.startDate != null
      ) {
        query = this.addParam(
          'startDate',
          dayjs(value.startDate).format(config.phpDateFormat),
          url,
          query,
        );
        query = this.addParam(
          'endDate',
          dayjs(value.endDate).format(config.phpDateFormat),
          url,
          query,
        );
      } else if (Array.isArray(value)) {
        if (value.length === 0) {
          continue;
        }
        query = this.addParam(key, value.join(','), url, query);
      } else {
        query = this.addParam(key, String(value), url, query);
      }
    }
    return query;
  }

  buildUrl(resource: string, queryParams?: QueryParams | null) {
    return this.url + resource + this.buildQuery(resource, queryParams);
  }

  isTokenExpired() {
    if (this.token == null) {
      return true;
    }

    const token = oauthClient.createToken(
      this.token.accessToken,
      this.token.refreshToken,
      this.token.tokenType,
      this.token.data as ClientOAuth2.Data,
    );

    // Build in a 10min buffer where we will proactively refresh the token if it is close to expiring
    const expiresDate = new Date(this.token.expires);
    expiresDate.setMinutes(expiresDate.getMinutes() - 10);
    token.expiresIn(expiresDate);

    return token.expired();
  }

  async refreshToken(): Promise<AuthToken | null> {
    // If we are already refreshing, don't attempt a second refresh (as the refresh_token will be invalid)
    if (this.refreshing) {
      await new Promise<void>((resolve) => {
        const interval = setInterval(() => {
          if (!this.refreshing) {
            clearInterval(interval);
            resolve();
          }
        }, 100);
      });
    }

    if (this.token == null) {
      return null;
    }

    if (!this.isTokenExpired()) {
      return this.token;
    }

    this.refreshing = true;

    try {
      const token = await store.dispatch(refreshToken(this.token)).unwrap();
      this.token = { ...token, client: null };
      return token;
    } catch (err) {
      // If we can't refresh the token, log out as no API requests will work
      this.token = null;
      store.dispatch(logout(true));
      throw err;
    } finally {
      this.refreshing = false;
    }
  }

  private request<T>(
    method: string,
    url: string,
    opts: RequestOptions,
  ): Promise<T> {
    // We load this file prior to loading the config. Check if the url has changed and update accordingly
    if (this.type == 'site') {
      if (this.url != config.api.url) {
        this.url = config.api.url;
      }
    } else if (this.type == 'central') {
      if (this.url != config.centralApi.url) {
        this.url = config.centralApi.url;
      }
    }

    const queryParams = opts.params || {};

    return this.refreshToken().then((token: AuthToken | null) => {
      const state = store.getState();

      const headers = Object.assign({}, this.headers, {
        Authorization: token ? `Bearer ${token.accessToken}` : 'None',
        'Content-Type':
          opts.contentType !== false
            ? opts.contentType || 'application/json'
            : undefined,
      });
      if (state.common.facility) {
        headers['X-Facility-ID'] = String(state.common.facility.id);
      }

      const api = axios.create({ baseURL: this.url });

      return api
        .request({
          url: this.buildUrl(url, queryParams),
          method,
          headers,
          data:
            opts.contentType !== false ? JSON.stringify(opts.data) : opts.data,
        })
        .then((response) => {
          return response.data;
        })
        .catch((err: AxiosError) => {
          if (err.response?.status == 401) {
            store.dispatch(logout(true));
          }

          if (err.response?.status == 403) {
            NotificationService.show({
              message: 'Permission denied',
              severity: NotificationSeverity.ERROR,
            });
          }

          return Promise.reject(
            err.response?.data ?? err.message ?? 'Unknown error',
          );
        });
    });
  }
}

export default ApiService;
